/**
 * Backstage is a real, separate room on the main MUC: the meeting's name plus
 * this suffix (szaki9 -> szaki9-backstage). It is NOT a breakout room on
 * purpose: breakout rooms are broadcast to every occupant of the main room, so
 * a "Backstage" breakout room (and who is in it) shows up in everyone's rooms
 * list, defeating the point. A separate room shares nothing with the main room
 * -- no rooms list entry, no occupant visibility, separate chat -- and jicofo
 * treats it as an ordinary conference, so no jicofo changes are needed.
 *
 * The server counterpart is in prosody's token/util.lib.lua verify_room, which
 * accepts a JWT whose room claim names the base meeting for its '-backstage'
 * room too (the claim check would otherwise reject the join).
 *
 * Go Live / Go backstage are room switches (leave without disconnect, rejoin),
 * the same mechanism breakout rooms use, so devices, mute states and the
 * virtual background carry over.
 */
export const BACKSTAGE_ROOM_SUFFIX = '-backstage';
/**
 * The meeting settings (carried in the token's {@code meeting.settings}) that
 * switch the backstage and the hide-me feature sets off for a meeting when
 * set to {@code 0}. Absent means on.
 */
export const ENABLE_BACKSTAGE_MODE_SETTING = 'ENABLE_BACKSTAGE_MODE';
export const ENABLE_HIDDEN_ME_SETTING = 'ENABLE_HIDDEN_ME';
/**
 * The {@code <text>} Prosody puts in the not-allowed error when it refuses a
 * self-initiated main-room join of someone a moderator is holding backstage
 * (mod_webinar_roles), and the breakout-room counterpart
 * (mod_muc_breakout_rooms). lib-meet-hour hands the text over as the only
 * parameter of {@code conference.connectionError.notAllowed}.
 */
export const BACKSTAGE_HELD_ERROR = 'backstage-held';
export const BREAKOUT_HELD_ERROR = 'breakout-held';
/**
 * The name of the server-verified message used to move a stage occupant between
 * the main room and backstage.
 */
export const WEBINAR_MOVE_REQUEST = 'webinar-move-request';
/**
 * Endpoint message used to synchronize the visitor-facing webinar layout.
 */
export const WEBINAR_AUDIENCE_LAYOUT = 'webinar-audience-layout';
/**
 * Endpoint message sent by a late-joining viewer to request the current layout.
 */
export const WEBINAR_AUDIENCE_LAYOUT_REQUEST = 'webinar-audience-layout-request';
/**
 * Room metadata key holding the moderator-set local-recording policy:
 * { allowed: boolean, updatedBy, updatedAt }. Its own top-level key on purpose —
 * the 'visitors' metadata object carries toggle intent that prosody re-applies,
 * so unrelated policies must not ride on it.
 */
export const LOCAL_RECORDING_METADATA_KEY = 'localRecording';
/**
 * Room metadata key holding the moderator-selected visitor-facing layout:
 * { showOnlyPanelists, stageOnlyMode }. Visitors on a vnode often never see the
 * original groupchat, so join-time metadata is the reliable copy.
 */
export const WEBINAR_AUDIENCE_LAYOUT_METADATA_KEY = 'webinarAudienceLayout';
/**
 * Groupchat json-message a main-room moderator sends to ask the server to bring
 * every backstage stage-occupant live. Prosody (mod_webinar_roles) verifies the
 * sender is a moderator, suppresses the message from the room, and relays a
 * moderator-stamped WEBINAR_MOVE_REQUEST to each backstage occupant.
 */
export const WEBINAR_BRING_ALL_LIVE = 'webinar-bring-all-live';
/**
 * Server -> moderator message carrying who is currently in the backstage room:
 * { occupants: [{ id, name, role }] }. The main room has no visibility into the
 * backstage room at all (that isolation is the point of using a separate room),
 * so selective promotion is only possible because the server publishes this.
 * Moderator-only, and deliberately the only crack in that isolation.
 */
export const WEBINAR_BACKSTAGE_ROSTER = 'webinar-backstage-roster';
/**
 * Groupchat json-message a main-room moderator sends to ask for the backstage
 * roster (on join, and whenever the list needs resyncing). Prosody verifies the
 * sender is a moderator and answers privately.
 */
export const WEBINAR_BACKSTAGE_ROSTER_REQUEST = 'webinar-backstage-roster-request';
/**
 * Server -> moderator message carrying who is currently in the MAIN room, sent
 * to moderators who are sitting backstage. The mirror of
 * WEBINAR_BACKSTAGE_ROSTER, and the same one deliberate crack in the isolation
 * between the two rooms: without it a moderator who steps backstage loses sight
 * of the meeting they are running.
 *
 * Rows carry mute and hidden state as well as name and role, because the
 * receiving client has no participant entry for these people to read it from.
 */
export const WEBINAR_MAIN_ROSTER = 'webinar-main-roster';
/**
 * Groupchat json-message a backstage moderator sends to ask for the main-room
 * roster. Prosody verifies the sender and answers privately.
 */
export const WEBINAR_MAIN_ROSTER_REQUEST = 'webinar-main-roster-request';
/**
 * Moderator controls that have to be able to target someone in the OTHER room
 * of the meeting — what the participants pane's "Main room" tab acts through
 * while the moderator is backstage.
 *
 * They are groupchat json-messages rather than IQs, unlike the same-room
 * hide/role controls, because the IQ forms live in lib-meet-hour's ChatRoom: a
 * new IQ would mean shipping a new lib for what the existing, already
 * server-verified message channel can carry. Prosody (mod_webinar_roles)
 * verifies the sender is a moderator, resolves the target in either room, and
 * suppresses the message so it never reaches the room.
 */
export const WEBINAR_HIDDEN_REQUEST = 'webinar-hidden-request';
export const WEBINAR_ROLE_REQUEST = 'webinar-role-request';
export const WEBINAR_KICK_REQUEST = 'webinar-kick-request';
/**
 * Doubles as the server -> target relay: a mute cannot go through Jicofo from
 * outside the conference, so the server asks the target's own client to mute
 * itself. Mute only — un-muting someone else needs their gesture, so the
 * control does not pretend to offer it.
 */
export const WEBINAR_MUTE_REQUEST = 'webinar-mute-request';
/**
 * Room-wide policies a moderator may set from backstage. Prosody applies both
 * to the main room's metadata, where the audience is, whatever room the sender
 * stands in: the room-metadata component only accepts a write from an occupant
 * of the room, and a moderator sitting backstage is not one.
 *
 * { name: WEBINAR_BLOCK_ALL_REQUEST, enabled: boolean }
 * { name: WEBINAR_LOCAL_RECORDING_REQUEST, allowed: boolean }
 */
export const WEBINAR_BLOCK_ALL_REQUEST = 'webinar-block-all-request';
export const WEBINAR_LOCAL_RECORDING_REQUEST = 'webinar-local-recording-request';
/**
 * Room metadata key for "block everyone's mic and camera":
 * {@code { enabled: boolean, updatedAt: number }}.
 *
 * Kept in room metadata rather than in the per-participant lock map for a
 * reason: that map is keyed by endpoint id and pushed by a moderator's client,
 * so it never contains someone who joins later and it is not in the room at
 * all while the moderator is backstage. Metadata reaches every occupant,
 * including a late joiner and a promoted visitor, from the room itself.
 */
export const MEDIA_BLOCK_ALL_METADATA_KEY = 'mediaBlockAll';
/**
 * Roles a viewer can hold, and which of them may still see a hidden occupant.
 *
 * The hidden flag is stored server-side with an audience rather than as a plain
 * boolean so that widening it later — letting panelists see hidden moderators,
 * say — is a change of this list and of what the UI offers, not a change of the
 * protocol. Prosody normalises whatever it is sent against the same set.
 *
 * Moderator is always in the audience: it is the only role that can un-hide
 * someone, and an occupant no one able to manage them can see is unmanageable.
 */
export const WEBINAR_HIDDEN_AUDIENCE_ROLES = [
    'moderator',
    'panelist',
    'presenter',
    'participant',
    'visitor'
];
export const WEBINAR_HIDDEN_DEFAULT_AUDIENCE = 'moderator';
/**
 * Participant property the client sets before joining to ask to arrive hidden.
 * It rides the join presence because a client that is not in the room yet has no
 * occupant to address the hide IQ to, and hiding after the join would let the
 * audience see the moderator appear first. Prosody honours it only for a
 * moderator and strips it from the presence either way.
 */
export const WEBINAR_HIDDEN_REQUEST_PROPERTY = 'webinarHiddenRequest';
